"use client";

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  useDroppable,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Calculator,
  Play,
  CheckCircle,
  XCircle,
  Search,
  GripVertical,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  Maximize2,
  Minimize2,
  FileSpreadsheet,
  ArrowRightLeft,
  Eye,
  ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormulaEditor, HighlightedFormula } from "@/components/ui/formula-editor";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  listAllOutputFieldGroups,
  createOutputFieldGroup,
  updateOutputFieldGroup,
  deleteOutputFieldGroup,
  createOutputField,
  updateOutputField,
  deleteOutputField,
  reorderOutputFieldGroups,
  reorderOutputFields,
  moveOutputField,
  testFormula,
  checkOutputFieldSlugUsage,
  type OutputFieldSlugUsageResponse,
  type OutputFieldSlugUsage,
} from "@/lib/api/output-fields";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { listAllVariableGroups } from "@/lib/api/variables";
import { getErrorMessage } from "@/lib/api/client";
import { slugify } from "@/lib/utils";
import type { OutputField, OutputType, FormulaTestResponse, OutputFieldGroupWithFields, FormFieldGroupWithFields, VariableGroupWithVariables, VisibilityConditions, ConditionOperator, ConditionLogic, FormField, VisibilityMode } from "@/types";

interface OutputFieldsTabProps {
  versionId: number;
  isEditable: boolean;
  openFieldSlug?: string | null;
  onOpenFieldSlugConsumed?: () => void;
}

const outputTypeLabels: Record<OutputType, string> = {
  number: "Nombre",
  text: "Texte",
  boolean: "Booléen",
  currency: "Devise",
  percentage: "Pourcentage",
  date: "Date",
  json: "JSON",
};

// Visibility mode labels
const visibilityModeLabels: Record<VisibilityMode, string> = {
  always: "Toujours affiché",
  never: "Jamais affiché",
  conditional: "Conditionnel",
};

// Operator labels for visibility conditions
const operatorLabels: Record<ConditionOperator, string> = {
  equals: "Égal à",
  not_equals: "Différent de",
  greater_than: "Supérieur à",
  greater_than_or_equals: "Supérieur ou égal à",
  less_than: "Inférieur à",
  less_than_or_equals: "Inférieur ou égal à",
  contains: "Contient",
  not_contains: "Ne contient pas",
  is_empty: "Est vide",
  is_not_empty: "N'est pas vide",
  in: "Dans la liste",
  not_in: "Pas dans la liste",
};

// Interface for field options with full path
interface FieldWithPathOption {
  field: FormField;
  fullPath: string;
  label: string;
}

// Field slug selector component for visibility conditions
interface FieldSlugSelectorProps {
  value: string;
  onSelect: (slug: string) => void;
  fields: FieldWithPathOption[];
  placeholder?: string;
  className?: string;
}

function FieldSlugSelector({ 
  value, 
  onSelect, 
  fields, 
  placeholder = "Sélectionner un champ...",
  className,
}: FieldSlugSelectorProps) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  // Find the selected field to display its fullPath
  const selectedField = fields.find((f) => f.field.slug === value);
  
  return (
    <Popover 
      open={open} 
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        // Reset tooltip state when popover opens
        if (isOpen) {
          setTooltipOpen(false);
        }
      }}
    >
      <Tooltip 
        open={open ? false : tooltipOpen} 
        onOpenChange={setTooltipOpen}
      >
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn("justify-between font-normal min-w-0", className)}
            >
              {selectedField ? (
                <span className="truncate font-mono text-xs">{selectedField.fullPath}</span>
              ) : (
                <span className="text-muted-foreground truncate">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {selectedField && (
          <TooltipContent side="top" className="max-w-md">
            <p className="font-mono text-xs">{selectedField.fullPath}</p>
            <p className="text-xs text-muted-foreground">{selectedField.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un champ..." />
          <CommandList>
            <CommandEmpty>Aucun champ trouvé.</CommandEmpty>
            <CommandGroup>
              {fields.map((item) => (
                <CommandItem
                  key={item.field.slug}
                  value={`${item.fullPath} ${item.label}`}
                  onSelect={() => {
                    onSelect(item.field.slug);
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-xs">{item.fullPath}</span>
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Sortable Field Component
interface SortableFieldProps {
  field: OutputField;
  isEditable: boolean;
  onEdit: (field: OutputField) => void;
  onDelete: (id: number) => void;
  onTest: (field: OutputField) => void;
  getTypeBadge: (type: OutputType) => React.ReactNode;
}

function SortableField({ field, isEditable, onEdit, onDelete, onTest, getTypeBadge }: SortableFieldProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `field-${field.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-4 rounded-lg bg-background"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {isEditable && (
          <button
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {!isEditable && <GripVertical className="h-4 w-4 text-muted-foreground opacity-30" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p
              className="font-medium cursor-pointer hover:underline hover:text-primary transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(field);
              }}
            >
              {field.label}
            </p>
            {getTypeBadge(field.output_type)}
            {field.visibility_mode === "never" && (
              <Badge variant="secondary">Masqué</Badge>
            )}
            {field.visibility_mode === "conditional" && field.visibility_conditions && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="gap-1">
                    <Eye className="h-3 w-3" />
                    Conditionnel
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">
                    Affichage conditionné par {field.visibility_conditions.conditions.length} condition{field.visibility_conditions.conditions.length > 1 ? "s" : ""}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {field.excel_source && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <FileSpreadsheet className="h-4 w-4 text-success cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-success" />
                    <div>
                      <p className="text-xs text-muted-foreground">Source Excel</p>
                      <p className="font-mono text-sm">{field.excel_source}</p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {field.slug}
            </code>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTest(field)}
        >
          <Play className="mr-1 h-3 w-3" />
          Tester
        </Button>

        {isEditable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(field)}>
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(field.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

// Field Preview for Drag Overlay
interface FieldDragPreviewProps {
  field: OutputField;
  getTypeBadge: (type: OutputType) => React.ReactNode;
}

function FieldDragPreview({ field, getTypeBadge }: FieldDragPreviewProps) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-background border-2 border-primary shadow-lg opacity-90">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium">{field.label}</p>
            {getTypeBadge(field.output_type)}
          </div>
          <p className="text-sm text-muted-foreground">
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {field.slug}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
}

// Droppable Group Zone for receiving fields
interface DroppableFieldZoneProps {
  groupId: number;
  children: React.ReactNode;
  isOver: boolean;
}

function DroppableFieldZone({ groupId, children, isOver }: DroppableFieldZoneProps) {
  const { setNodeRef } = useDroppable({
    id: `droppable-group-${groupId}`,
    data: {
      type: "group",
      groupId,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[60px] rounded-lg transition-all duration-200 ${
        isOver 
          ? "bg-primary/10 border-2 border-dashed border-primary ring-2 ring-primary/20" 
          : ""
      }`}
    >
      {children}
      {isOver && (
        <div className="flex items-center justify-center gap-2 py-3 text-primary text-sm font-medium">
          <ArrowRightLeft className="h-4 w-4" />
          Déposer ici pour déplacer
        </div>
      )}
    </div>
  );
}

// Sortable Group Component
interface SortableGroupProps {
  group: OutputFieldGroupWithFields;
  depth: number;
  isEditable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEditGroup: (group: OutputFieldGroupWithFields) => void;
  onDeleteGroup: (id: number) => void;
  onAddSubGroup: (group: OutputFieldGroupWithFields) => void;
  onAddField: (groupId: number) => void;
  onEditField: (field: OutputField) => void;
  onDeleteField: (id: number) => void;
  onTestField: (field: OutputField) => void;
  getTypeBadge: (type: OutputType) => React.ReactNode;
  onChildGroupDragEnd: (parentId: number, activeId: string, overId: string) => void;
  renderChildGroup: (child: OutputFieldGroupWithFields, depth: number) => React.ReactNode;
  expandedGroups: Set<number>;
  onToggleGroup: (groupId: number) => void;
  activeFieldId: string | null;
  overGroupId: number | null;
}

function SortableGroup({
  group,
  depth,
  isEditable,
  isExpanded,
  onToggle,
  onEditGroup,
  onDeleteGroup,
  onAddSubGroup,
  onAddField,
  onEditField,
  onDeleteField,
  onTestField,
  getTypeBadge,
  onChildGroupDragEnd,
  renderChildGroup,
  expandedGroups,
  onToggleGroup,
  activeFieldId,
  overGroupId,
}: SortableGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `group-${group.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginLeft: depth * 16,
  };

  const hasContent = group.fields.length > 0 || group.children.length > 0;
  const sortedFields = useMemo(
    () => [...group.fields].sort((a, b) => a.order - b.order),
    [group.fields]
  );
  const fieldIds = useMemo(
    () => sortedFields.map((f) => `field-${f.id}`),
    [sortedFields]
  );
  const sortedChildren = useMemo(
    () => [...group.children].sort((a, b) => a.order - b.order),
    [group.children]
  );
  const childGroupIds = useMemo(
    () => sortedChildren.map((c) => `group-${c.id}`),
    [sortedChildren]
  );

  const childGroupSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChildGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onChildGroupDragEnd(group.id, String(active.id), String(over.id));
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isEditable && (
            <button
              className="cursor-grab active:cursor-grabbing touch-none"
              onClick={(e) => e.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          {!isEditable && <div className="w-4" />}
          {hasContent ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="w-4" />
          )}
          <FolderOpen className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium">{group.name}</p>
            <p className="text-sm text-muted-foreground">
              {group.fields.length} calcul{group.fields.length > 1 ? "s" : ""}
              {group.children.length > 0 && `, ${group.children.length} sous-groupe${group.children.length > 1 ? "s" : ""}`}
            </p>
          </div>
        </div>

        {isEditable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddField(group.id);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Ajouter un calcul
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSubGroup(group);
                }}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Ajouter un sous-groupe
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEditGroup(group);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteGroup(group.id);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isExpanded && (
        <div className="border-t px-4 py-2 bg-muted/30 space-y-2">
          {/* Fields - with droppable zone for cross-group drag */}
          {/* Only show droppable zone if there are fields OR if there are no children (to show empty state) */}
          {(group.fields.length > 0 || group.children.length === 0) && (
            <DroppableFieldZone 
              groupId={group.id} 
              isOver={activeFieldId !== null && overGroupId === group.id}
            >
              {group.fields.length === 0 && group.children.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {activeFieldId ? "Déposer le calcul ici" : "Aucun calcul dans ce groupe"}
                </p>
              ) : (
                <>
                  {isEditable ? (
                    <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
                      {sortedFields.map((field) => (
                        <SortableField
                          key={field.id}
                          field={field}
                          isEditable={isEditable}
                          onEdit={onEditField}
                          onDelete={onDeleteField}
                          onTest={onTestField}
                          getTypeBadge={getTypeBadge}
                        />
                      ))}
                    </SortableContext>
                  ) : (
                    sortedFields.map((field) => (
                      <SortableField
                        key={field.id}
                        field={field}
                        isEditable={isEditable}
                        onEdit={onEditField}
                        onDelete={onDeleteField}
                        onTest={onTestField}
                        getTypeBadge={getTypeBadge}
                      />
                    ))
                  )}
                </>
              )}
            </DroppableFieldZone>
          )}

          {/* Children groups */}
          {group.children.length > 0 && (
            <div className="space-y-2">
              {isEditable && !activeFieldId ? (
                // Only use nested DndContext for child group reordering when NOT dragging a field
                <DndContext
                  sensors={childGroupSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleChildGroupDragEnd}
                >
                  <SortableContext items={childGroupIds} strategy={verticalListSortingStrategy}>
                    {sortedChildren.map((child) => renderChildGroup(child, depth + 1))}
                  </SortableContext>
                </DndContext>
              ) : (
                // When dragging a field or not editable, just render children without nested DndContext
                sortedChildren.map((child) => renderChildGroup(child, depth + 1))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OutputFieldsTab({ versionId, isEditable, openFieldSlug, onOpenFieldSlugConsumed }: OutputFieldsTabProps) {
  const queryClient = useQueryClient();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingOpenSlug, setPendingOpenSlug] = useState<string | null>(null);

  // Group dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<OutputFieldGroupWithFields | null>(null);
  const [groupForm, setGroupForm] = useState({
    name: "",
    slug: "",
    description: "",
    order: 0,
    parent_id: null as number | null,
  });

  // Field dialog states
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [isEditingField, setIsEditingField] = useState(false);
  const [selectedField, setSelectedField] = useState<OutputField | null>(null);
  const [fieldDialogExpanded, setFieldDialogExpanded] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<FormulaTestResponse | null>(null);
  const [testInputs, setTestInputs] = useState<string>("{}");
  const [testFormInputs, setTestFormInputs] = useState<Record<string, string>>({});
  const [detectedDependencies, setDetectedDependencies] = useState<string[]>([]);
  const [isDetectingDeps, setIsDetectingDeps] = useState(false);
  const [createNewGroupInFieldDialog, setCreateNewGroupInFieldDialog] = useState(false);
  const [newGroupInFieldDialog, setNewGroupInFieldDialog] = useState({
    name: "",
    slug: "",
    description: "",
    parent_id: null as number | null,
  });

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    label: "",
    description: "",
    implementation_note: "",
    formula: "",
    output_type: "number" as OutputType,
    format_pattern: "",
    decimal_places: 2,
    prefix: "",
    suffix: "",
    group_id: 0,
    order: 0,
    visibility_mode: "always" as VisibilityMode,
    visibility_conditions: null as VisibilityConditions | null,
    excel_source: "",
  });

  // Slug usage tracking
  const [slugUsageInfo, setSlugUsageInfo] = useState<OutputFieldSlugUsageResponse | null>(null);
  const [isLoadingSlugUsage, setIsLoadingSlugUsage] = useState(false);

  // Field drag state for cross-group dragging
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<OutputField | null>(null);
  const [overGroupId, setOverGroupId] = useState<number | null>(null);
  const [sourceGroupId, setSourceGroupId] = useState<number | null>(null);

  // Queries
  const { data: groups, isLoading } = useQuery({
    queryKey: ["output-field-groups", versionId],
    queryFn: () => listAllOutputFieldGroups(versionId),
  });

  // Query for form field groups (for dependency mapping)
  const { data: formFieldGroups } = useQuery({
    queryKey: ["form-field-groups-all", versionId],
    queryFn: () => listAllFormFieldGroups(versionId),
  });

  // Query for variables (for dependency mapping)
  const { data: variableGroups } = useQuery({
    queryKey: ["variable-groups-all", versionId],
    queryFn: () => listAllVariableGroups(versionId),
  });

  // Build a map of slug -> {name, order, type} for all possible dependencies
  const dependencyMap = useMemo(() => {
    const map: Record<string, { name: string; order: number; type: "form" | "output" | "variable" }> = {};
    
    // Add form fields (from all groups recursively, preserving order)
    const addFormFields = (groupList: FormFieldGroupWithFields[], baseOrder: number = 0) => {
      groupList.forEach((group, groupIndex) => {
        const groupOffset = baseOrder + groupIndex * 1000;
        group.fields.forEach((field) => {
          map[field.slug] = { name: field.name, order: groupOffset + field.order, type: "form" };
        });
        addFormFields(group.children, groupOffset + 100);
      });
    };
    if (formFieldGroups) {
      addFormFields(formFieldGroups);
    }
    
    // Add output fields (from all groups recursively)
    const addOutputFields = (groupList: OutputFieldGroupWithFields[], baseOrder: number = 0) => {
      groupList.forEach((group, groupIndex) => {
        const groupOffset = baseOrder + groupIndex * 1000;
        group.fields.forEach((field) => {
          map[field.slug] = { name: field.name, order: groupOffset + field.order, type: "output" };
        });
        addOutputFields(group.children, groupOffset + 100);
      });
    };
    if (groups) {
      addOutputFields(groups);
    }
    
    // Add variables
    if (variableGroups) {
      variableGroups.forEach((group: VariableGroupWithVariables, groupIndex: number) => {
        group.variables.forEach((variable, varIndex) => {
          map[variable.slug] = { name: variable.name, order: 10000 + groupIndex * 100 + varIndex, type: "variable" };
        });
      });
    }
    
    return map;
  }, [formFieldGroups, groups, variableGroups]);

  // Build list of form fields with full path for visibility conditions
  const formFieldsWithFullPath = useMemo(() => {
    const result: FieldWithPathOption[] = [];
    
    const processGroups = (groups: FormFieldGroupWithFields[], parentPath: string = "") => {
      groups.forEach((group) => {
        const currentPath = parentPath ? `${parentPath} > ${group.name}` : group.name;
        group.fields.forEach((field) => {
          result.push({
            field,
            fullPath: `${currentPath} > ${field.slug}`,
            label: field.label,
          });
        });
        processGroups(group.children, currentPath);
      });
    };
    
    if (formFieldGroups) {
      processGroups(formFieldGroups);
    }
    
    return result;
  }, [formFieldGroups]);

  // Flatten all form fields for visibility condition evaluation
  const allFormFields = useMemo(() => {
    const result: FormField[] = [];
    
    const processGroups = (groups: FormFieldGroupWithFields[]) => {
      groups.forEach((group) => {
        result.push(...group.fields);
        processGroups(group.children);
      });
    };
    
    if (formFieldGroups) {
      processGroups(formFieldGroups);
    }
    
    return result;
  }, [formFieldGroups]);

  // Handle navigation from validation errors
  useEffect(() => {
    if (openFieldSlug) {
      setPendingOpenSlug(openFieldSlug);
      onOpenFieldSlugConsumed?.();
    }
  }, [openFieldSlug, onOpenFieldSlugConsumed]);

  // Helper function to find a field by slug recursively
  const findFieldBySlug = (groupList: OutputFieldGroupWithFields[], slug: string): OutputField | null => {
    for (const group of groupList) {
      const field = group.fields.find(f => f.slug === slug);
      if (field) return field;
      const childField = findFieldBySlug(group.children, slug);
      if (childField) return childField;
    }
    return null;
  };

  // Open field dialog when data is loaded and there's a pending slug
  useEffect(() => {
    if (pendingOpenSlug && groups && !isLoading) {
      const fieldToOpen = findFieldBySlug(groups, pendingOpenSlug);
      if (fieldToOpen) {
        setTimeout(() => {
          handleEditField(fieldToOpen);
        }, 100);
      }
      setPendingOpenSlug(null);
    }
  }, [pendingOpenSlug, groups, isLoading]);

  // Filter groups and fields recursively based on search query
  const filteredGroups = useMemo(() => {
    if (!groups || !searchQuery.trim()) return groups;
    
    const query = searchQuery.toLowerCase().trim();
    
    const filterGroupRecursively = (group: OutputFieldGroupWithFields): OutputFieldGroupWithFields | null => {
      // Filter fields that match the search query
      const matchingFields = group.fields.filter(
        (field) =>
          field.slug.toLowerCase().includes(query) ||
          field.name.toLowerCase().includes(query) ||
          field.label.toLowerCase().includes(query)
      );
      
      // Recursively filter children
      const filteredChildren = group.children
        .map(filterGroupRecursively)
        .filter((child): child is OutputFieldGroupWithFields => child !== null);
      
      // Check if group matches
      const groupMatches = group.slug.toLowerCase().includes(query) || group.name.toLowerCase().includes(query);
      
      // Include group if it matches, has matching fields, or has matching children
      if (groupMatches || matchingFields.length > 0 || filteredChildren.length > 0) {
        return {
          ...group,
          fields: groupMatches ? group.fields : matchingFields,
          children: filteredChildren,
        };
      }
      return null;
    };
    
    return groups
      .map(filterGroupRecursively)
      .filter((group): group is OutputFieldGroupWithFields => group !== null);
  }, [groups, searchQuery]);

  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: ["output-field-groups", versionId] });
  };

  // Flatten all groups for selection
  const flattenGroups = (groups: OutputFieldGroupWithFields[]): { group: OutputFieldGroupWithFields; depth: number }[] => {
    const result: { group: OutputFieldGroupWithFields; depth: number }[] = [];
    const flatten = (groups: OutputFieldGroupWithFields[], depth: number) => {
      for (const group of groups) {
        result.push({ group, depth });
        flatten(group.children, depth + 1);
      }
    };
    flatten(groups, 0);
    return result;
  };

  const flatGroups = groups ? flattenGroups(groups) : [];

  // Group mutations
  const createGroupMutation = useMutation({
    mutationFn: (data: typeof groupForm) => createOutputFieldGroup(versionId, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe créé");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof groupForm> }) =>
      updateOutputFieldGroup(versionId, id, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe mis à jour");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => deleteOutputFieldGroup(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Field mutations
  const createFieldMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      let groupId = data.group_id;
      
      // If creating a new group, do it first
      if (createNewGroupInFieldDialog && newGroupInFieldDialog.name) {
        const newGroup = await createOutputFieldGroup(versionId, {
          ...newGroupInFieldDialog,
          slug: newGroupInFieldDialog.slug || slugify(newGroupInFieldDialog.name),
          order: 0,
        });
        groupId = newGroup.id;
      }
      
      return createOutputField(versionId, {
        ...data,
        group_id: groupId,
      });
    },
    onSuccess: () => {
      invalidateGroups();
      toast.success("Calcul créé");
      closeFieldDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateFieldMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof formData> }) =>
      updateOutputField(versionId, id, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Calcul mis à jour");
      closeFieldDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (id: number) => deleteOutputField(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Calcul supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const testMutation = useMutation({
    mutationFn: (data: { formula: string; inputs: Record<string, unknown> }) =>
      testFormula(versionId, { formula: data.formula, test_inputs: data.inputs }),
    onSuccess: (result) => {
      setTestResult(result);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Reorder mutations
  const reorderGroupsMutation = useMutation({
    mutationFn: (groupOrders: { id: number; order: number }[]) =>
      reorderOutputFieldGroups(versionId, groupOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  const reorderFieldsMutation = useMutation({
    mutationFn: (fieldOrders: { id: number; order: number }[]) =>
      reorderOutputFields(versionId, fieldOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  const moveFieldMutation = useMutation({
    mutationFn: ({ fieldId, targetGroupId, newOrder }: { fieldId: number; targetGroupId: number; newOrder: number }) =>
      moveOutputField(versionId, fieldId, targetGroupId, newOrder),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Calcul déplacé vers un autre groupe");
    },
    onError: (error) => toast.error("Erreur lors du déplacement", { description: getErrorMessage(error) }),
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handlers
  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setIsEditingGroup(false);
    setSelectedGroup(null);
    setGroupForm({ name: "", slug: "", description: "", order: 0, parent_id: null });
  };

  const closeFieldDialog = () => {
    setFieldDialogOpen(false);
    setIsEditingField(false);
    setSelectedField(null);
    setFieldDialogExpanded(false);
    setCreateNewGroupInFieldDialog(false);
    setNewGroupInFieldDialog({ name: "", slug: "", description: "", parent_id: null });
    setSlugUsageInfo(null);
    setIsLoadingSlugUsage(false);
    setFormData({
      name: "",
      slug: "",
      label: "",
      description: "",
      implementation_note: "",
      formula: "",
      output_type: "number",
      format_pattern: "",
      decimal_places: 2,
      prefix: "",
      suffix: "",
      group_id: 0,
      order: 0,
      visibility_mode: "always",
      visibility_conditions: null,
      excel_source: "",
    });
  };

  const handleEditGroup = (group: OutputFieldGroupWithFields) => {
    setSelectedGroup(group);
    setGroupForm({
      name: group.name,
      slug: group.slug,
      description: group.description || "",
      order: group.order,
      parent_id: group.parent_id,
    });
    setIsEditingGroup(true);
    setGroupDialogOpen(true);
  };

  const handleAddSubGroup = (parentGroup: OutputFieldGroupWithFields) => {
    setGroupForm({
      name: "",
      slug: "",
      description: "",
      order: 0,
      parent_id: parentGroup.id,
    });
    setIsEditingGroup(false);
    setGroupDialogOpen(true);
  };

  const handleAddField = (groupId: number) => {
    // Find the group to calculate the next order
    const findGroup = (groups: OutputFieldGroupWithFields[]): OutputFieldGroupWithFields | undefined => {
      for (const group of groups) {
        if (group.id === groupId) return group;
        const found = findGroup(group.children);
        if (found) return found;
      }
      return undefined;
    };
    
    const targetGroup = groups ? findGroup(groups) : undefined;
    const maxOrder = targetGroup?.fields?.length 
      ? Math.max(...targetGroup.fields.map(f => f.order)) + 1 
      : 0;
    
    setFormData({
      ...formData,
      group_id: groupId,
      order: maxOrder,
    });
    setFieldDialogOpen(true);
  };

  const handleEditField = async (field: OutputField) => {
    setSelectedField(field);
    setFormData({
      name: field.name,
      slug: field.slug,
      label: field.label,
      description: field.description || "",
      implementation_note: field.implementation_note || "",
      formula: field.formula,
      output_type: field.output_type,
      format_pattern: field.format_pattern || "",
      // Use ?? to preserve 0 as valid value (0 decimal places = integers)
      decimal_places: field.decimal_places ?? 2,
      prefix: field.prefix || "",
      suffix: field.suffix || "",
      group_id: field.group_id,
      order: field.order,
      visibility_mode: field.visibility_mode,
      visibility_conditions: field.visibility_conditions,
      excel_source: field.excel_source || "",
    });
    setIsEditingField(true);
    setFieldDialogOpen(true);
    
    // Check if slug is used elsewhere
    setIsLoadingSlugUsage(true);
    try {
      const usageInfo = await checkOutputFieldSlugUsage(versionId, field.id);
      setSlugUsageInfo(usageInfo);
    } catch {
      // If check fails, assume slug is used (safer)
      setSlugUsageInfo({ slug: field.slug, is_used: true, usages: [], can_edit_slug: false });
    } finally {
      setIsLoadingSlugUsage(false);
    }
  };

  const handleTest = async (field: OutputField) => {
    setSelectedField(field);
    setFormData({ ...formData, formula: field.formula });
    setTestResult(null);
    setTestInputs("{}");
    setTestFormInputs({});
    setDetectedDependencies([]);
    setTestDialogOpen(true);
    
    // Detect dependencies by running a probe test with empty inputs
    setIsDetectingDeps(true);
    try {
      const probeResult = await testFormula(versionId, { 
        formula: field.formula, 
        test_inputs: {} 
      });
      if (probeResult.dependencies.length > 0) {
        setDetectedDependencies(probeResult.dependencies);
        // Initialize form inputs for each dependency
        const initialInputs: Record<string, string> = {};
        probeResult.dependencies.forEach(dep => {
          initialInputs[dep] = "";
        });
        setTestFormInputs(initialInputs);
      }
    } catch {
      // Ignore errors during probe, user will see them when they test
    } finally {
      setIsDetectingDeps(false);
    }
  };

  const runTest = () => {
    // Build inputs from form fields if we have detected dependencies
    if (detectedDependencies.length > 0) {
      const inputs: Record<string, unknown> = {};
      for (const dep of detectedDependencies) {
        const value = testFormInputs[dep];
        if (value !== undefined && value !== "") {
          // Try to parse as number or JSON, otherwise keep as string
          const trimmed = value.trim();
          if (trimmed === "true") {
            inputs[dep] = true;
          } else if (trimmed === "false") {
            inputs[dep] = false;
          } else if (trimmed === "null") {
            inputs[dep] = null;
          } else if (!isNaN(Number(trimmed)) && trimmed !== "") {
            inputs[dep] = Number(trimmed);
          } else if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
              inputs[dep] = JSON.parse(trimmed);
            } catch {
              inputs[dep] = value;
            }
          } else {
            inputs[dep] = value;
          }
        }
      }
      testMutation.mutate({ formula: formData.formula, inputs });
    } else {
      // Fallback to JSON textarea
      try {
        const inputs = JSON.parse(testInputs);
        testMutation.mutate({ formula: formData.formula, inputs });
      } catch {
        toast.error("JSON invalide pour les inputs");
      }
    }
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: isEditingField ? formData.slug : slugify(name),
      label: isEditingField ? formData.label : name,
    });
  };

  const getTypeBadge = (type: OutputType) => {
    const colors: Record<OutputType, string> = {
      number: "bg-info/20 text-info",
      text: "bg-success/20 text-success",
      boolean: "bg-laposte-navy/20 text-laposte-navy",
      currency: "bg-yellow-500/20 text-yellow-400",
      percentage: "bg-laposte-magenta/20 text-laposte-magenta",
      date: "bg-warning/20 text-warning",
      json: "bg-laposte-teal/20 text-laposte-teal",
    };
    return <Badge className={colors[type]}>{outputTypeLabels[type]}</Badge>;
  };

  // Helper to find a group by ID recursively
  const findGroupById = (groupList: OutputFieldGroupWithFields[], targetId: number): OutputFieldGroupWithFields | undefined => {
    for (const group of groupList) {
      if (group.id === targetId) return group;
      const found = findGroupById(group.children, targetId);
      if (found) return found;
    }
    return undefined;
  };

  // Helper to find which group a field belongs to
  const findFieldGroupId = (groupList: OutputFieldGroupWithFields[], fieldId: number): number | null => {
    for (const group of groupList) {
      if (group.fields.some(f => f.id === fieldId)) return group.id;
      const childResult = findFieldGroupId(group.children, fieldId);
      if (childResult) return childResult;
    }
    return null;
  };

  // Helper to find a field by ID recursively
  const findFieldById = (groupList: OutputFieldGroupWithFields[], fieldId: number): OutputField | null => {
    for (const group of groupList) {
      const field = group.fields.find(f => f.id === fieldId);
      if (field) return field;
      const childResult = findFieldById(group.children, fieldId);
      if (childResult) return childResult;
    }
    return null;
  };

  // Handle group drag end
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !groups) return;

    const activeId = Number(String(active.id).replace("group-", ""));
    const overId = Number(String(over.id).replace("group-", ""));

    const oldIndex = groups.findIndex((g) => g.id === activeId);
    const newIndex = groups.findIndex((g) => g.id === overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newGroups = arrayMove(groups, oldIndex, newIndex);
    const groupOrders = newGroups.map((g, index) => ({
      id: g.id,
      order: index,
    }));

    reorderGroupsMutation.mutate(groupOrders);
  };

  // Handle field drag start
  const handleFieldDragStart = (event: DragStartEvent) => {
    if (!groups) return;
    const activeIdStr = String(event.active.id);
    
    if (activeIdStr.startsWith("field-")) {
      const fieldId = Number(activeIdStr.replace("field-", ""));
      const field = findFieldById(groups, fieldId);
      const groupId = findFieldGroupId(groups, fieldId);
      
      setActiveFieldId(activeIdStr);
      setActiveField(field);
      setSourceGroupId(groupId);
    }
  };

  // Handle field drag over (to detect which group we're hovering)
  const handleFieldDragOver = (event: DragOverEvent) => {
    const { over } = event;
    
    if (!over) {
      setOverGroupId(null);
      return;
    }

    const overIdStr = String(over.id);
    
    // Check if hovering over a droppable group zone
    if (overIdStr.startsWith("droppable-group-")) {
      const groupId = Number(overIdStr.replace("droppable-group-", ""));
      setOverGroupId(groupId);
    } else if (overIdStr.startsWith("field-")) {
      // Hovering over a field - find its group
      if (groups) {
        const fieldId = Number(overIdStr.replace("field-", ""));
        const groupId = findFieldGroupId(groups, fieldId);
        setOverGroupId(groupId);
      }
    } else {
      setOverGroupId(null);
    }
  };

  // Handle field drag end - supports both reordering within group and moving to another group
  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Reset drag state
    setActiveFieldId(null);
    setActiveField(null);
    setOverGroupId(null);
    setSourceGroupId(null);
    
    if (!over || !groups || !sourceGroupId) return;

    const activeIdStr = String(active.id);
    if (!activeIdStr.startsWith("field-")) return;
    
    const fieldId = Number(activeIdStr.replace("field-", ""));
    const overIdStr = String(over.id);
    
    // Determine target group
    let targetGroupId: number | null = null;
    
    if (overIdStr.startsWith("droppable-group-")) {
      targetGroupId = Number(overIdStr.replace("droppable-group-", ""));
    } else if (overIdStr.startsWith("field-")) {
      const overFieldId = Number(overIdStr.replace("field-", ""));
      targetGroupId = findFieldGroupId(groups, overFieldId);
    }
    
    if (!targetGroupId) return;

    // Check if moving to a different group
    if (targetGroupId !== sourceGroupId) {
      // Moving to a different group
      const targetGroup = findGroupById(groups, targetGroupId);
      const newOrder = targetGroup?.fields.length ?? 0;
      
      moveFieldMutation.mutate({
        fieldId,
        targetGroupId,
        newOrder,
      });
    } else {
      // Same group - reordering within the group
      if (overIdStr.startsWith("field-")) {
        const overFieldId = Number(overIdStr.replace("field-", ""));
        if (fieldId === overFieldId) return;
        
        const group = findGroupById(groups, sourceGroupId);
        if (!group) return;
        
        const sortedFields = [...group.fields].sort((a, b) => a.order - b.order);
        const oldIndex = sortedFields.findIndex((f) => f.id === fieldId);
        const newIndex = sortedFields.findIndex((f) => f.id === overFieldId);
        
        if (oldIndex === -1 || newIndex === -1) return;
        
        const newFields = arrayMove(sortedFields, oldIndex, newIndex);
        const fieldOrders = newFields.map((f, index) => ({
          id: f.id,
          order: index,
        }));
        
        reorderFieldsMutation.mutate(fieldOrders);
      }
    }
  };

  // Handle child group drag end within a parent group
  const handleChildGroupDragEnd = (parentId: number, activeIdStr: string, overIdStr: string) => {
    if (!groups) return;

    const parentGroup = findGroupById(groups, parentId);
    if (!parentGroup) return;

    const activeGroupIdNum = Number(activeIdStr.replace("group-", ""));
    const overGroupIdNum = Number(overIdStr.replace("group-", ""));

    const sortedChildren = [...parentGroup.children].sort((a, b) => a.order - b.order);
    const oldIndex = sortedChildren.findIndex((g) => g.id === activeGroupIdNum);
    const newIndex = sortedChildren.findIndex((g) => g.id === overGroupIdNum);

    if (oldIndex === -1 || newIndex === -1) return;

    const newChildren = arrayMove(sortedChildren, oldIndex, newIndex);
    const groupOrders = newChildren.map((g, index) => ({
      id: g.id,
      order: index,
    }));

    reorderGroupsMutation.mutate(groupOrders);
  };

  // Render child group with proper depth
  const renderChildGroup = (group: OutputFieldGroupWithFields, depth: number): React.ReactNode => (
    <SortableGroup
      key={group.id}
      group={group}
      depth={depth}
      isEditable={isEditable}
      isExpanded={expandedGroups.has(group.id)}
      onToggle={() => toggleGroup(group.id)}
      onEditGroup={handleEditGroup}
      onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
      onAddSubGroup={handleAddSubGroup}
      onAddField={handleAddField}
      onEditField={handleEditField}
      onDeleteField={(id) => deleteFieldMutation.mutate(id)}
      onTestField={handleTest}
      getTypeBadge={getTypeBadge}
      onChildGroupDragEnd={handleChildGroupDragEnd}
      renderChildGroup={renderChildGroup}
      expandedGroups={expandedGroups}
      onToggleGroup={toggleGroup}
      activeFieldId={activeFieldId}
      overGroupId={overGroupId}
    />
  );

  // Sorted groups for DnD (use filtered groups when searching)
  const sortedGroups = useMemo(
    () => filteredGroups ? [...filteredGroups].sort((a, b) => a.order - b.order) : [],
    [filteredGroups]
  );
  const groupIds = useMemo(
    () => sortedGroups.map((g) => `group-${g.id}`),
    [sortedGroups]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasNoGroups = !groups || groups.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Champs de sortie (Calculs)</CardTitle>
          <CardDescription>
            Définissez les formules de calcul et les résultats affichés
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-[220px]"
            />
          </div>
          {isEditable && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setGroupDialogOpen(true)}>
                <FolderPlus className="mr-2 h-4 w-4" />
                Nouveau groupe
              </Button>
              {!hasNoGroups && (
                <Button onClick={() => setFieldDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau calcul
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {hasNoGroups ? (
          <div className="text-center py-12 text-muted-foreground">
            <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="mb-2">Aucun groupe de calculs</p>
            <p className="text-sm mb-4">
              Créez d&apos;abord un groupe pour pouvoir ajouter des calculs
            </p>
            {isEditable && (
              <Button
                variant="outline"
                onClick={() => setGroupDialogOpen(true)}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Créer un groupe
              </Button>
            )}
          </div>
        ) : sortedGroups.length === 0 && searchQuery ? (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucun résultat pour &quot;{searchQuery}&quot;</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setSearchQuery("")}
            >
              Effacer la recherche
            </Button>
          </div>
        ) : isEditable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={(args) => {
              // First check for droppable zones (groups)
              const droppableCollisions = pointerWithin(args);
              if (droppableCollisions.length > 0) {
                return droppableCollisions;
              }
              // Fall back to rect intersection for sortable items
              return rectIntersection(args);
            }}
            onDragStart={handleFieldDragStart}
            onDragOver={handleFieldDragOver}
            onDragEnd={(event) => {
              const activeIdStr = String(event.active.id);
              if (activeIdStr.startsWith("field-")) {
                handleFieldDragEnd(event);
              } else if (activeIdStr.startsWith("group-")) {
                handleGroupDragEnd(event);
              }
            }}
          >
            <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {sortedGroups.map((group) => (
                  <SortableGroup
                    key={group.id}
                    group={group}
                    depth={0}
                    isEditable={isEditable}
                    isExpanded={expandedGroups.has(group.id)}
                    onToggle={() => toggleGroup(group.id)}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                    onAddSubGroup={handleAddSubGroup}
                    onAddField={handleAddField}
                    onEditField={handleEditField}
                    onDeleteField={(id) => deleteFieldMutation.mutate(id)}
                    onTestField={handleTest}
                    getTypeBadge={getTypeBadge}
                    onChildGroupDragEnd={handleChildGroupDragEnd}
                    renderChildGroup={renderChildGroup}
                    expandedGroups={expandedGroups}
                    onToggleGroup={toggleGroup}
                    activeFieldId={activeFieldId}
                    overGroupId={overGroupId}
                  />
                ))}
              </div>
            </SortableContext>
            
            {/* Drag overlay for field preview */}
            <DragOverlay>
              {activeField ? (
                <FieldDragPreview field={activeField} getTypeBadge={getTypeBadge} />
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="space-y-3">
            {sortedGroups.map((group) => (
              <SortableGroup
                key={group.id}
                group={group}
                depth={0}
                isEditable={isEditable}
                isExpanded={expandedGroups.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
                onEditGroup={handleEditGroup}
                onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                onAddSubGroup={handleAddSubGroup}
                onAddField={handleAddField}
                onEditField={handleEditField}
                onDeleteField={(id) => deleteFieldMutation.mutate(id)}
                onTestField={handleTest}
                getTypeBadge={getTypeBadge}
                onChildGroupDragEnd={handleChildGroupDragEnd}
                renderChildGroup={renderChildGroup}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                activeFieldId={activeFieldId}
                overGroupId={overGroupId}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={closeGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditingGroup ? "Modifier le groupe" : "Nouveau groupe"}
            </DialogTitle>
            <DialogDescription>
              {groupForm.parent_id
                ? "Ce groupe sera créé comme sous-groupe"
                : "Créez un groupe pour organiser vos calculs"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Nom</Label>
              <Input
                id="group-name"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm({
                    ...groupForm,
                    name: e.target.value,
                    slug: isEditingGroup ? groupForm.slug : slugify(e.target.value),
                  })
                }
                placeholder="Ex: Résultats principaux"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-slug">Slug</Label>
              <Input
                id="group-slug"
                value={groupForm.slug}
                onChange={(e) => setGroupForm({ ...groupForm, slug: e.target.value })}
              />
              {isEditingGroup && (
                <p className="text-xs text-muted-foreground">
                  Attention : modifier le slug peut impacter les références existantes
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-parent">Groupe parent</Label>
              <Select
                value={groupForm.parent_id?.toString() || "none"}
                onValueChange={(value) =>
                  setGroupForm({
                    ...groupForm,
                    parent_id: value !== "none" ? parseInt(value) : null,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucun (groupe racine)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (groupe racine)</SelectItem>
                  {flatGroups
                    .filter(({ group }) => group.id !== selectedGroup?.id)
                    .map(({ group, depth }) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {"─".repeat(depth)} {group.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeGroupDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditingGroup && selectedGroup
                  ? updateGroupMutation.mutate({ id: selectedGroup.id, data: groupForm })
                  : createGroupMutation.mutate(groupForm)
              }
              disabled={createGroupMutation.isPending || updateGroupMutation.isPending || !groupForm.name}
            >
              {isEditingGroup ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={closeFieldDialog}>
        <DialogContent className={`flex flex-col transition-all duration-200 ${
          fieldDialogExpanded 
            ? "max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh]" 
            : "max-w-2xl max-h-[90vh]"
        }`}>
          <DialogHeader className="flex-shrink-0 pr-16">
            <DialogTitle>
              {isEditingField ? "Modifier le calcul" : "Nouveau calcul"}
            </DialogTitle>
          </DialogHeader>
          <button
            type="button"
            onClick={() => setFieldDialogExpanded(!fieldDialogExpanded)}
            className="absolute right-10 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            title={fieldDialogExpanded ? "Réduire" : "Agrandir"}
          >
            {fieldDialogExpanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
          <div className="grid grid-cols-2 gap-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label htmlFor="output-name">Nom technique</Label>
              <Input
                id="output-name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-slug">Slug</Label>
              <Input
                id="output-slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                disabled={isEditingField && (isLoadingSlugUsage || !slugUsageInfo?.can_edit_slug)}
              />
              {isEditingField && isLoadingSlugUsage && (
                <p className="text-xs text-muted-foreground">Vérification des dépendances...</p>
              )}
              {isEditingField && !isLoadingSlugUsage && slugUsageInfo && !slugUsageInfo.can_edit_slug && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-xs text-warning hover:underline cursor-pointer text-left">
                      Champ utilisé, impossible de modifier le slug (voir où)
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Utilisations du slug "{slugUsageInfo.slug}"</h4>
                      <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                        {slugUsageInfo.usages.map((usage: OutputFieldSlugUsage, index: number) => (
                          <li key={index} className="border-l-2 border-warning pl-2 py-1">
                            <div className="font-medium">{usage.entity_name}</div>
                            <div className="text-xs text-muted-foreground">{usage.description}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-label">Libellé</Label>
              <Input
                id="output-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-type">Type de sortie</Label>
              <Select
                value={formData.output_type}
                onValueChange={(value) =>
                  setFormData({ ...formData, output_type: value as OutputType })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(outputTypeLabels).map(([type, label]) => (
                    <SelectItem key={type} value={type}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Group selection */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="field-group">Groupe</Label>
                {!isEditingField && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCreateNewGroupInFieldDialog(!createNewGroupInFieldDialog)}
                  >
                    {createNewGroupInFieldDialog ? (
                      <>
                        <Calculator className="h-4 w-4 mr-1" />
                        Annuler
                      </>
                    ) : (
                      <>
                        <FolderPlus className="h-4 w-4 mr-1" />
                        Nouveau groupe
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {createNewGroupInFieldDialog ? (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="space-y-2">
                    <Label>Nom du nouveau groupe</Label>
                    <Input
                      value={newGroupInFieldDialog.name}
                      onChange={(e) =>
                        setNewGroupInFieldDialog({
                          ...newGroupInFieldDialog,
                          name: e.target.value,
                          slug: slugify(e.target.value),
                        })
                      }
                      placeholder="Ex: Résultats principaux"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Groupe parent (optionnel)</Label>
                    <Select
                      value={newGroupInFieldDialog.parent_id?.toString() || "none"}
                      onValueChange={(value) =>
                        setNewGroupInFieldDialog({
                          ...newGroupInFieldDialog,
                          parent_id: value !== "none" ? parseInt(value) : null,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucun (groupe racine)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun (groupe racine)</SelectItem>
                        {flatGroups.map(({ group, depth }) => (
                          <SelectItem key={group.id} value={group.id.toString()}>
                            {"─".repeat(depth)} {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <Select
                  value={formData.group_id?.toString() || ""}
                  onValueChange={(value) =>
                    setFormData({ ...formData, group_id: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un groupe..." />
                  </SelectTrigger>
                  <SelectContent>
                    {flatGroups.map(({ group, depth }) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {"─".repeat(depth)} {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="col-span-2 space-y-2 flex flex-col">
              <Label htmlFor="output-formula">Formule</Label>
              <FormulaEditor
                id="output-formula"
                value={formData.formula}
                onChange={(value) => setFormData({ ...formData, formula: value })}
                placeholder={`poids * tarif_kg + frais_fixes

# Ou avec variables locales:
cout = lookup("ma_table", "colonne", zone=zone)
cout * quantite`}
                rows={fieldDialogExpanded ? 15 : 5}
                className={fieldDialogExpanded ? "flex-1 min-h-[200px]" : ""}
              />
              <p className="text-xs text-muted-foreground">
                Utilisez les slugs des champs, variables et calculs. Vous pouvez définir des variables locales avec <code className="bg-muted px-1 rounded">nom = expression</code> sur plusieurs lignes.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-decimals">Décimales</Label>
              <Input
                id="output-decimals"
                type="number"
                min="0"
                value={formData.decimal_places}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value);
                  // Use ?? to allow 0 as valid value, default to 2 only for NaN (empty/invalid input)
                  setFormData({ ...formData, decimal_places: isNaN(parsed) ? 2 : parsed });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-order">Ordre</Label>
              <Input
                id="output-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-prefix">Préfixe</Label>
              <Input
                id="output-prefix"
                value={formData.prefix}
                onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                placeholder="Ex: €, $"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="output-suffix">Suffixe</Label>
              <Input
                id="output-suffix"
                value={formData.suffix}
                onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
                placeholder="Ex: mois, kg, %"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="output-description">Description</Label>
              <Textarea
                id="output-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="output-implementation-note">Note d&apos;implémentation</Label>
              <Textarea
                id="output-implementation-note"
                value={formData.implementation_note}
                onChange={(e) => setFormData({ ...formData, implementation_note: e.target.value })}
                placeholder="Notes techniques pour les développeurs..."
                rows={2}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="output-excel-source" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-success" />
                Source Excel
              </Label>
              <Input
                id="output-excel-source"
                value={formData.excel_source}
                onChange={(e) => setFormData({ ...formData, excel_source: e.target.value })}
                placeholder="Ex: 'Costing Transport 2025'!H7"
                className="font-mono text-sm"
              />
            </div>

            {/* Visibility Mode */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <Label>Mode de visibilité</Label>
              </div>
              <Select
                value={formData.visibility_mode}
                onValueChange={(value: VisibilityMode) => {
                  if (value === "conditional" && !formData.visibility_conditions) {
                    // Initialize conditions when switching to conditional mode
                    setFormData({
                      ...formData,
                      visibility_mode: value,
                      visibility_conditions: {
                        logic: "AND" as ConditionLogic,
                        conditions: [{ field_slug: "", operator: "is_not_empty" as ConditionOperator }],
                      },
                    });
                  } else if (value !== "conditional") {
                    // Clear conditions when switching away from conditional mode
                    setFormData({
                      ...formData,
                      visibility_mode: value,
                      visibility_conditions: null,
                    });
                  } else {
                    setFormData({ ...formData, visibility_mode: value });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(visibilityModeLabels).map(([mode, label]) => (
                    <SelectItem key={mode} value={mode}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.visibility_mode === "always" && "Ce champ sera toujours affiché dans les résultats."}
                {formData.visibility_mode === "never" && "Ce champ ne sera jamais affiché (mais peut être utilisé dans d'autres formules)."}
                {formData.visibility_mode === "conditional" && "Ce champ sera affiché uniquement si les conditions ci-dessous sont remplies."}
              </p>

              {/* Conditional visibility settings */}
              {formData.visibility_mode === "conditional" && formData.visibility_conditions && (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">Afficher ce résultat si</span>
                    <Select
                      value={formData.visibility_conditions.logic}
                      onValueChange={(value: ConditionLogic) =>
                        setFormData({
                          ...formData,
                          visibility_conditions: {
                            ...formData.visibility_conditions!,
                            logic: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">Toutes</SelectItem>
                        <SelectItem value="OR">Au moins une</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">des conditions sont remplies :</span>
                  </div>

                  <div className="space-y-2">
                    {formData.visibility_conditions.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2 flex-wrap">
                        <FieldSlugSelector
                          value={condition.field_slug}
                          onSelect={(slug) => {
                            const newConditions = [...formData.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], field_slug: slug };
                            setFormData({
                              ...formData,
                              visibility_conditions: {
                                ...formData.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                          fields={formFieldsWithFullPath}
                          placeholder="Champ..."
                          className="flex-1 min-w-[150px]"
                        />

                        <Select
                          value={condition.operator}
                          onValueChange={(value: ConditionOperator) => {
                            const newConditions = [...formData.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], operator: value };
                            setFormData({
                              ...formData,
                              visibility_conditions: {
                                ...formData.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(operatorLabels).map(([op, label]) => (
                              <SelectItem key={op} value={op}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {!["is_empty", "is_not_empty"].includes(condition.operator) && (
                          (() => {
                            const selectedField = allFormFields.find(f => f.slug === condition.field_slug);
                            const isCheckbox = selectedField?.field_type === "checkbox";
                            const isSelect = selectedField?.field_type === "select" || selectedField?.field_type === "multiselect" || selectedField?.field_type === "radio";
                            const hasEnumValues = isSelect && selectedField?.enum_values && selectedField.enum_values.length > 0;
                            const showSelectForValue = ["equals", "not_equals", "in", "not_in"].includes(condition.operator);
                            
                            if (isCheckbox) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...formData.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFormData({
                                      ...formData,
                                      visibility_conditions: {
                                        ...formData.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="w-24">
                                    <SelectValue placeholder="Valeur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">Coché</SelectItem>
                                    <SelectItem value="false">Non coché</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            if (hasEnumValues && showSelectForValue) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...formData.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFormData({
                                      ...formData,
                                      visibility_conditions: {
                                        ...formData.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1 min-w-[100px]">
                                    <SelectValue placeholder="Valeur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {selectedField.enum_values!.map((enumVal) => (
                                      <SelectItem key={enumVal} value={enumVal}>
                                        {enumVal}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            return (
                              <Input
                                className="flex-1 min-w-[100px]"
                                placeholder="Valeur..."
                                value={String(condition.value ?? "")}
                                onChange={(e) => {
                                  const newConditions = [...formData.visibility_conditions!.conditions];
                                  newConditions[index] = { ...newConditions[index], value: e.target.value };
                                  setFormData({
                                    ...formData,
                                    visibility_conditions: {
                                      ...formData.visibility_conditions!,
                                      conditions: newConditions,
                                    },
                                  });
                                }}
                              />
                            );
                          })()
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newConditions = formData.visibility_conditions!.conditions.filter(
                              (_, i) => i !== index
                            );
                            if (newConditions.length === 0) {
                              // Keep at least one empty condition
                              setFormData({
                                ...formData,
                                visibility_conditions: {
                                  ...formData.visibility_conditions!,
                                  conditions: [{ field_slug: "", operator: "is_not_empty" as ConditionOperator }],
                                },
                              });
                            } else {
                              setFormData({
                                ...formData,
                                visibility_conditions: {
                                  ...formData.visibility_conditions!,
                                  conditions: newConditions,
                                },
                              });
                            }
                          }}
                          className="text-destructive hover:text-destructive h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        visibility_conditions: {
                          ...formData.visibility_conditions!,
                          conditions: [
                            ...formData.visibility_conditions!.conditions,
                            { field_slug: "", operator: "is_not_empty" as ConditionOperator },
                          ],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une condition
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" onClick={closeFieldDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditingField && selectedField
                  ? updateFieldMutation.mutate({ id: selectedField.id, data: formData })
                  : createFieldMutation.mutate(formData)
              }
              disabled={
                createFieldMutation.isPending || 
                updateFieldMutation.isPending || 
                (!createNewGroupInFieldDialog && !formData.group_id) ||
                (createNewGroupInFieldDialog && !newGroupInFieldDialog.name)
              }
            >
              {isEditingField ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Tester la formule</DialogTitle>
            <DialogDescription>
              {selectedField?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
            <HighlightedFormula 
              code={selectedField?.formula || formData.formula} 
              className="max-h-40 overflow-y-auto"
            />
            
            {/* Form-based inputs for detected dependencies */}
            {isDetectingDeps ? (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="animate-pulse">Détection des dépendances...</span>
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </div>
            ) : detectedDependencies.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Valeurs à renseigner pour le test</Label>
                  <Badge variant="secondary">{detectedDependencies.length} dépendance{detectedDependencies.length > 1 ? "s" : ""}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 max-h-[300px] overflow-y-auto pr-2">
                  {/* Sort dependencies by their order in the form */}
                  {[...detectedDependencies]
                    .sort((a, b) => {
                      const orderA = dependencyMap[a]?.order ?? 99999;
                      const orderB = dependencyMap[b]?.order ?? 99999;
                      return orderA - orderB;
                    })
                    .map((dep) => {
                      const depInfo = dependencyMap[dep];
                      const displayName = depInfo?.name || dep;
                      return (
                        <div key={dep} className="space-y-1.5 p-3 rounded-lg border bg-muted/30">
                          <Label htmlFor={`test-input-${dep}`} className="text-sm block">
                            <span className="font-medium block mb-1">{displayName}</span>
                            <code className="text-xs px-1.5 py-0.5 bg-background rounded text-muted-foreground block truncate" title={dep}>
                              {dep}
                            </code>
                          </Label>
                          <Input
                            id={`test-input-${dep}`}
                            value={testFormInputs[dep] || ""}
                            onChange={(e) => setTestFormInputs({
                              ...testFormInputs,
                              [dep]: e.target.value
                            })}
                            placeholder="Valeur..."
                            className="font-mono"
                          />
                        </div>
                      );
                    })}
                </div>
                <p className="text-xs text-muted-foreground">
                  💡 Les nombres sont auto-détectés. Pour du texte, saisissez directement sans guillemets.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Inputs (JSON)</Label>
                <Textarea
                  value={testInputs}
                  onChange={(e) => setTestInputs(e.target.value)}
                  placeholder='{"poids": 5, "zone": "A"}'
                  rows={4}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Aucune dépendance externe détectée, ou formule auto-suffisante.
                </p>
              </div>
            )}

            {testResult && (
              <div
                className={`p-4 rounded-lg ${
                  testResult.success
                    ? "bg-success/10 border border-success/30"
                    : "bg-error/10 border border-error/30"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-success" />
                  ) : (
                    <XCircle className="h-5 w-5 text-error" />
                  )}
                  <span className="font-medium">
                    {testResult.success ? "Succès" : "Erreur"}
                  </span>
                </div>
                {testResult.success ? (
                  <>
                    {/* Local variables (intermediate values) */}
                    {testResult.local_variables && Object.keys(testResult.local_variables).length > 0 && (
                      <div className="mb-3 space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Variables locales :</p>
                        <div className="bg-background/50 rounded p-2 space-y-1 max-h-40 overflow-y-auto">
                          {Object.entries(testResult.local_variables).map(([name, value]) => (
                            <div key={name} className="flex items-center gap-2 font-mono text-sm">
                              <span className="text-info">{name}</span>
                              <span className="text-muted-foreground">=</span>
                              <span className="text-success break-all">{JSON.stringify(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="font-mono text-lg break-all">
                      Résultat: <span className="text-success">{JSON.stringify(testResult.result)}</span>
                    </p>
                  </>
                ) : (
                  <p className="text-error break-words whitespace-pre-wrap max-h-40 overflow-y-auto">{testResult.error}</p>
                )}
                {testResult.dependencies.length > 0 && !testResult.success && detectedDependencies.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2 break-words">
                    Dépendances externes: {testResult.dependencies.join(", ")}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              Fermer
            </Button>
            <Button onClick={runTest} disabled={testMutation.isPending || isDetectingDeps}>
              <Play className="mr-2 h-4 w-4" />
              {testMutation.isPending ? "Test..." : "Tester"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
